import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks';
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics';
import { buildImgSrc } from '@/helpers/image-url';
import { Icon, Image, tk, Typography, useToken } from '@sunwebgroup/ui-components';
import useEmblaCarousel from 'embla-carousel-react';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { styled } from 'styled-components';
import type { CMSRoomType } from '@/cms/fields/mappers/productDetailPageFieldsMapper';

const Wrapper = styled.div`
  position: relative;
`;

const FaddingWrapper = styled.div`
  @media ${tk('mq_tablet')} {
    &:before {
      background-image: linear-gradient(270deg, hsla(0, 0%, 100%, 0), ${tk('theme_color_neutral_100')});
      bottom: 0;
      content: '';
      display: block;
      left: 0;
      position: absolute;
      width: ${tk('spacing_2xl')};
      height: 100%;
      z-index: 1;
    }

    &:after {
      background-image: linear-gradient(90deg, hsla(0, 0%, 100%, 0), ${tk('theme_color_neutral_100')});
      bottom: 0;
      content: '';
      display: block;
      right: 0;
      position: absolute;
      width: ${tk('spacing_2xl')};
      height: 100%;
      z-index: 1;
    }
  }
`;

const Slider = styled.div`
  overflow: hidden;
  height: 100%;
`;

const SliderContainer = styled.div`
  display: flex;
  height: 100%;

  @media ${tk('mq_tablet')} {
    padding-top: ${tk('spacing_2xl')};
  }
`;

const Slide = styled.div`
  flex: 0 0 100%;
  @media ${tk('mq_tablet')} {
    flex: unset;
    margin-right: ${tk('spacing_xs')};

    &:first-child {
      margin-left: ${tk('spacing_2xl')};
    }

    &:last-child {
      margin-right: ${tk('spacing_2xl')};
    }
  }
`;

const SlideWrapper = styled.button`
  border-radius: ${tk('radius_large')};
  overflow: hidden;
  align-content: center;
  width: 100%;
`;

const SlideImage = styled(Image.Root)`
  object-fit: cover;

  @media (max-width: ${tk('breakpoint_tablet')}) {
    width: 100%;
  }
`;

const Dots = styled.div`
  position: absolute;
  bottom: ${tk('spacing_md')};
  width: 100%;
  display: flex;
  justify-content: center;

  @media ${tk('mq_tablet')} {
    display: none;
  }
`;

const Dot = styled.div<{ $active: boolean }>`
  width: ${tk('spacing_xs')};
  height: ${tk('spacing_xs')};
  border-radius: 50%;
  background-color: ${(props) =>
    props.$active ? tk('color_palette_grey_50') : tk('color_palette_transparent_white_75')};
  margin: 0 ${tk('spacing_2xs')};
`;

interface GalleryProps {
  roomType: CMSRoomType;
  handleDetailImageClick: (index: number) => void;
}

export function Gallery({ roomType, handleDetailImageClick }: GalleryProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    loop: false,
    dragFree: false,
    breakpoints: {
      [`${useToken<string>('mq_tablet')}`]: { dragFree: true },
    },
  });
  const intl = useIntl();
  const [slidesInView, setSlidesInView] = useState<number[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scrollSnaps, setScrollSnaps] = useState<number[]>([]);
  const { sendEvent } = useAnalytics();

  const findSlidesInView = useCallback(() => {
    if (!emblaApi) return;

    setSlidesInView((slidesInViewElements) => {
      if (slidesInViewElements.length === emblaApi.slideNodes().length) {
        emblaApi.off('slidesInView', findSlidesInView);
      }
      const inView = emblaApi.slidesInView().filter((index) => !slidesInViewElements.includes(index));
      return slidesInViewElements.concat(inView);
    });
  }, [emblaApi, setSlidesInView]);

  const onSelect = useCallback(() => {
    if (emblaApi !== undefined) {
      const index = emblaApi.selectedScrollSnap();
      setSelectedIndex(index);
    }
  }, [emblaApi]);

  const handleImageClick = (index: number) => {
    handleDetailImageClick(index);
    sendEvent(
      createPDPClickEvent({
        element: 'Slider',
        description: 'Roomtype images',
        result: `Open image ${index + 1}`,
        type: roomType.name,
      })
    );
  };

  useEffect(() => {
    if (!emblaApi) return;

    setScrollSnaps(emblaApi.scrollSnapList() ?? []);
    emblaApi.on('init', findSlidesInView);
    emblaApi.on('reInit', findSlidesInView);
    emblaApi.on('slidesInView', findSlidesInView);
    emblaApi.on('select', onSelect);
    emblaApi.reInit();
  }, [emblaApi, findSlidesInView, onSelect]);

  return (
    <Wrapper>
      <FaddingWrapper>
        <Slider ref={emblaRef}>
          <SliderContainer>
            {roomType.imageUrls.map((image, index) => (
              <Slide key={index}>
                <SlideWrapper onClick={() => handleImageClick(index)}>
                  <SlideImage
                    defaultSource={{
                      src: buildImgSrc(image, 480, 270),
                      width: 480,
                      height: 270,
                    }}
                    loaderSource={{
                      src: buildImgSrc(image, 20),
                    }}
                    sources={[
                      {
                        src: buildImgSrc(image, 240, 240),
                        width: 160,
                        height: 120,
                        breakpoint: 'breakpoint_tablet',
                      },
                    ]}
                    alternativeText={roomType.name}
                    startImmediateLoad={slidesInView.includes(index)}
                  >
                    <Image.Error>
                      <Icon glyph="IconNoImage" size="lg" />
                      <Typography $variant="body-md-bold">
                        {intl.formatMessage({ id: 'roomtypes_image_error' })}
                      </Typography>
                    </Image.Error>
                  </SlideImage>
                </SlideWrapper>
              </Slide>
            ))}
          </SliderContainer>
        </Slider>
      </FaddingWrapper>
      {roomType.imageUrls.length > 1 && (
        <Dots>
          {scrollSnaps.map((_, index) => (
            <Dot $active={index === selectedIndex} key={index} />
          ))}
        </Dots>
      )}
    </Wrapper>
  );
}
